<script setup lang="ts">
/**
 * 确认页。
 *
 * 这一页是「顾客最后一次看清楚要付多少钱」的地方，所以：
 *   1. 金额全部来自服务端试算（进页面就重新试算一次，避免金额是几分钟前的旧值）；
 *   2. 试算发现的售罄/下架项会当场标出来并从购物车摘掉，顾客不会点了半天才发现；
 *   3. 「确认下单」按钮要够大、位置固定，且下单中要禁用 —— 触屏上重复提交太常见了。
 */
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { formatYuan, mulCents } from '@jinhua/shared';
import { kiosk } from '../stores/kiosk';

const router = useRouter();

/** 每行小计：用 shared 的整数分乘法，绝不出现浮点误差 */
function lineTotal(row: { unitPriceCents: number; quantity: number }): string {
  return formatYuan(mulCents(row.unitPriceCents, row.quantity));
}

const preview = computed(() => kiosk.state.preview);
const issues = computed(() => preview.value?.issues ?? []);
const items = computed(() => kiosk.state.cart);
const busy = computed(() => kiosk.state.busy);

onMounted(async () => {
  // 进页面重新试算一次：顾客可能在购物车里停留了好几分钟
  if (kiosk.state.cart.length > 0) await kiosk.previewOrder();
});

async function submit(): Promise<void> {
  const result = await kiosk.submitOrder();
  if (result === 'menu-changed') {
    // 菜单变了：留在本页，让顾客看到新的金额和提示
    return;
  }
  if (result) void router.push('/pay');
}

function back(): void {
  void router.push('/menu');
}
</script>

<template>
  <div class="screen">
    <div class="topbar">
      <div>
        <div class="topbar__title">确认订单</div>
        <div class="topbar__sub">请核对菜品和金额，确认后出付款二维码</div>
      </div>
      <button class="btn btn--ghost" data-testid="back-menu" @click="back">继续点餐</button>
    </div>

    <div class="confirm screen__body">
      <section class="confirm__list panel">
        <div v-for="row in items" :key="row.key" class="line">
          <div class="line__main">
            <div class="line__name">{{ row.productName }}</div>
            <div v-if="row.variantText || row.remark" class="line__sub muted">
              <span v-if="row.variantText">{{ row.variantText }}</span>
              <span v-if="row.remark"> · {{ row.remark }}</span>
            </div>
          </div>
          <div class="line__ops">
            <button class="qty" data-testid="qty-minus" @click="kiosk.changeQuantity(row.key, -1)">−</button>
            <span class="qty__value">{{ row.quantity }}</span>
            <button class="qty" data-testid="qty-plus" @click="kiosk.changeQuantity(row.key, 1)">＋</button>
          </div>
          <div class="line__price amount">¥{{ lineTotal(row) }}</div>
          <button class="line__del" @click="kiosk.removeItem(row.key)">✕</button>
        </div>

        <div v-if="items.length === 0" class="muted center empty-line">
          购物车是空的，回上一页点几道菜吧
        </div>
      </section>

      <aside class="confirm__side">
        <div v-if="issues.length > 0" class="banner banner--danger issue-banner">
          <div>
            <div v-for="issue in issues" :key="issue.index">· {{ issue.message }}</div>
            <div class="muted">这些菜已从购物车去掉，请重新确认</div>
          </div>
        </div>

        <div class="panel sums">
          <div class="sums__row">
            <span class="muted">菜品小计</span>
            <span class="amount">{{ preview ? `¥${preview.amounts.itemsTotalText}` : '—' }}</span>
          </div>
          <div class="sums__row">
            <span class="muted">优惠</span>
            <span class="amount">{{ preview ? `-¥${preview.amounts.discountText}` : '—' }}</span>
          </div>
          <div class="sums__row">
            <span class="muted">餐盒费</span>
            <span class="amount">{{ preview ? `¥${preview.amounts.packFeeText}` : '—' }}</span>
          </div>
          <div class="sums__divider" />
          <div class="sums__row sums__row--total">
            <span>应付</span>
            <span class="amount amount--big" data-testid="payable">
              {{ preview ? `¥${preview.amounts.payableText}` : '—' }}
            </span>
          </div>
        </div>

        <div class="muted confirm__note">
          金额由系统核算，屏幕显示多少就付多少
        </div>

        <button
          class="btn btn--primary btn--xl btn--block"
          data-testid="submit-order"
          :disabled="busy || items.length === 0 || preview?.canSubmit === false"
          @click="submit"
        >
          {{ busy ? '提交中…' : '确认下单' }}
        </button>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.confirm {
  display: flex;
  gap: 16px;
}

.confirm__list {
  flex: 1;
  min-width: 0;
  overflow: auto;
  padding: 8px;
}

.line {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
}

.line:last-of-type {
  border-bottom: none;
}

.line__main {
  flex: 1;
  min-width: 0;
}

.line__name {
  font-size: 22px;
  font-weight: 700;
}

.line__sub {
  font-size: 15px;
  margin-top: 4px;
}

.line__ops {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qty {
  width: 62px;
  height: 62px;
  border-radius: 12px
;
  background: var(--panel-2);
  border: 2px solid var(--line);
  font-size: 26px;
  font-weight: 700;
}

.qty__value {
  min-width: 44px;
  text-align: center;
  font-size: 22px;
  font-weight: 800;
}

.line__price {
  min-width: 120px;
  text-align: right;
  font-size: 24px;
  color: #ffd08a;
}

.line__del {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  color: var(--muted);
  font-size: 22px;
}

.confirm__side {
  width: min(420px, 38vw);
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.issue-banner {
  align-items: flex-start;
}

.sums {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sums__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 19px;
}

.sums__row--total {
  font-size: 22px;
  font-weight: 700;
}

.sums__divider {
  height: 1px;
  background: var(--line);
}

.empty-line {
  padding: 40px;
}

.confirm__note {
  font-size: 15px;
  text-align: center;
  margin-top: auto;
}

@media (max-aspect-ratio: 1/1) {
  .confirm {
    flex-direction: column;
  }

  .confirm__side {
    width: 100%;
  }
}

/* 窄屏（手机竖屏）与竖屏一律改成上下布局。
   注意这里用逗号是「或」：矮宽的手机横屏也要走这一套。 */
@media (max-width: 860px), (max-aspect-ratio: 1/1) {
  .confirm {
    flex-direction: column;
    /* 整页一起滚：菜品多的时候，确认按钮在下面也能滚到 */
    overflow: auto;
  }

  .confirm__list {
    overflow: visible;
    flex: 0 0 auto;
    padding: 4px;
  }

  .confirm__side {
    width: 100%;
    flex: 0 0 auto;
  }
}

@media (max-width: 860px) {
  /* 手机上菜名独占一行：不然长菜名会被挤成三行竖排，读起来很费劲 */
  .line {
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 6px;
    row-gap: 6px;
  }

  .line__main {
    flex: 1 1 100%;
  }

  .line__price {
    margin-left: auto;
  }

  .line__name {
    font-size: 17px;
  }

  .line__sub {
    font-size: 13px;
  }

  .qty {
    width: 46px;
    height: 46px;
    font-size: 20px;
    border-radius: 10px;
  }

  .qty__value {
    min-width: 30px;
    font-size: 18px;
  }

  .line__price {
    min-width: 74px;
    font-size: 17px;
  }

  .line__del {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .sums {
    padding: 14px;
    gap: 8px;
  }

  .sums__row {
    font-size: 16px;
  }

  .sums__row--total {
    font-size: 19px;
  }

  .confirm__note {
    display: none;
  }
}

</style>
